import type { SocialLink } from '@/api/endpoints'
import { buildApiUrl } from '@/api/client'
import type { IconType } from 'react-icons'
import { SiTelegram, SiVk, SiYoutube } from 'react-icons/si'
import { isRestrictedSocialLink } from '@/utils/restrictedSocials'

type GlobalSocialKind = 'telegram' | 'vk' | 'youtube' | 'other'

const badgeBaseClass =
  'flex w-40 shrink-0 items-center gap-2.5 overflow-hidden rounded-full px-4 py-2.5 text-base font-medium transition-opacity hover:opacity-90 no-underline shadow-sm'

function detectGlobalSocialKind(title: string, link: string): GlobalSocialKind {
  let host = ''
  try {
    host = new URL(link).hostname.toLowerCase()
  } catch {
    return matchTitleKeywords(title)
  }

  if (host === 't.me' || host.endsWith('.t.me') || host.includes('telegram.')) {
    return 'telegram'
  }
  if (host.includes('vk.com') || host.includes('vk.ru') || host.includes('vkontakte')) {
    return 'vk'
  }
  if (host.includes('youtube.com') || host.includes('youtu.be')) {
    return 'youtube'
  }
  return matchTitleKeywords(title)
}

function matchTitleKeywords(title: string): GlobalSocialKind {
  const t = title.toLowerCase()
  if (t.includes('telegram') || t.includes('телеграм')) return 'telegram'
  if (t.includes('вконтакте') || /\bvk\b/.test(t)) return 'vk'
  if (t.includes('youtube') || t.includes('ютуб')) return 'youtube'
  return 'other'
}

const KIND_META: Record<
  GlobalSocialKind,
  { label: string; className: string; Icon?: IconType }
> = {
  telegram: {
    label: 'Telegram',
    className: 'bg-[#229ED9] text-white',
    Icon: SiTelegram,
  },
  vk: {
    label: 'ВКонтакте',
    className: 'bg-[#0077FF] text-white',
    Icon: SiVk,
  },
  youtube: {
    label: 'YouTube',
    className: 'bg-[#FF0000] text-white',
    Icon: SiYoutube,
  },
  other: {
    label: '',
    className:
      'bg-white/10 text-gray-100 border border-white/20 hover:bg-white/15',
  },
}

export function GlobalSocialLinkBadge({ social }: { social: SocialLink }) {
  if (isRestrictedSocialLink(social)) {
    return null
  }

  const kind = detectGlobalSocialKind(social.title, social.link)
  const meta = KIND_META[kind]
  const label = kind === 'other' ? social.title : meta.label
  const Icon = meta.Icon

  return (
    <a
      target="_blank"
      rel="noreferrer"
      href={social.link}
      aria-label={social.title}
      title={social.title}
      className={`${badgeBaseClass} ${meta.className}`}
    >
      <div className="mx-auto flex flex-row items-center gap-2">
        {Icon ? (
        <Icon className="h-6 w-6 shrink-0" aria-hidden />
      ) : social.icon ? (
        <img
          src={buildApiUrl(social.icon)}
          alt=""
          className="h-6 w-6 shrink-0 object-contain opacity-90"
          loading="lazy"
        />
      ) : null}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      </div>
    </a>
  )
}
